import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ContextSession } from "../../services/ContextSession";
import {
  actualizarColoniaMantenimiento,
  crearColoniaMantenimiento,
  listarColoniasMantenimiento,
  obtenerColoniaMantenimiento,
} from "./enrolamientoApi";

const EMPTY_FORM = {
  coloniaId: "",
  nombre: "",
  departamentoId: "",
  municipioId: "",
};

const EMPTY_FILTERS = {
  id: "",
  nombre: "",
  departamentoId: "",
  municipioId: "",
};

const EMPTY_PAGINATION = {
  page: 1,
  page_size: 25,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

export default function ColoniasEnrolamiento() {
  const session = useContext(ContextSession);
  const authToken = session?.getToken ? session.getToken() : "";

  const [items, setItems] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [modalOpen, setModalOpen] = useState(false);
  const [generatingColoniaId, setGeneratingColoniaId] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const coloniaIdRequestRef = useRef(0);

  const municipiosFiltradosForm = useMemo(() => {
    if (!form.departamentoId) return municipios;
    return municipios.filter(
      (item) => String(item?.FKDepartamentoId || "") === String(form.departamentoId),
    );
  }, [municipios, form.departamentoId]);

  const municipiosFiltradosFiltro = useMemo(() => {
    if (!filters.departamentoId) return municipios;
    return municipios.filter(
      (item) => String(item?.FKDepartamentoId || "") === String(filters.departamentoId),
    );
  }, [municipios, filters.departamentoId]);

  const filterId = String(filters.id || "").trim().toUpperCase();
  const filterNombre = String(filters.nombre || "").trim().toUpperCase();
  const filterDepartamentoId = String(filters.departamentoId || "").trim();
  const filterMunicipioId = String(filters.municipioId || "").trim();
  const filteredItems = items;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listarColoniasMantenimiento(authToken, {
        includeCatalogos: true,
        page: page + 1,
        pageSize: rowsPerPage,
        id: filterId,
        nombre: filterNombre,
        departamentoId: filterDepartamentoId,
        municipioId: filterMunicipioId,
      });
      const rows = Array.isArray(result?.items) ? result.items : [];
      const paginationData = result?.pagination || EMPTY_PAGINATION;
      const catalogos = result?.catalogos || {};
      setItems(rows);
      setPagination(paginationData);
      setDepartamentos(Array.isArray(catalogos?.departamentos) ? catalogos.departamentos : []);
      setMunicipios(Array.isArray(catalogos?.municipios) ? catalogos.municipios : []);
      const serverPage = Math.max((Number(paginationData?.page) || 1) - 1, 0);
      if (serverPage !== page) {
        setPage(serverPage);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el mantenimiento de colonias.");
    } finally {
      setLoading(false);
    }
  }, [
    authToken,
    page,
    rowsPerPage,
    filterId,
    filterNombre,
    filterDepartamentoId,
    filterMunicipioId,
  ]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    setPage(0);
  }, [filterId, filterNombre, filterDepartamentoId, filterMunicipioId]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
    setGeneratingColoniaId(false);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleEdit = (row) => {
    setEditingId(String(row?.ColoniaId || ""));
    setForm({
      coloniaId: String(row?.ColoniaId || ""),
      nombre: String(row?.Nombre || ""),
      departamentoId: String(row?.FKDepartamentoId || ""),
      municipioId: String(row?.FKMunicipioId || ""),
    });
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const generarColoniaIdDisponible = useCallback(
    async (municipioIdValue = "") => {
      const municipioId = String(municipioIdValue || "").trim();
      if (!municipioId || editingId) return;

      const requestId = coloniaIdRequestRef.current + 1;
      coloniaIdRequestRef.current = requestId;
      setGeneratingColoniaId(true);

      try {
        const usados = new Set();
        let nextPage = 1;
        let hasNext = true;
        let guard = 0;

        while (hasNext && guard < 100) {
          guard += 1;
          const result = await listarColoniasMantenimiento(authToken, {
            municipioId,
            page: nextPage,
            pageSize: 200,
          });
          if (coloniaIdRequestRef.current !== requestId) return;

          const rows = Array.isArray(result?.items) ? result.items : [];
          rows.forEach((row) => {
            const coloniaId = String(row?.ColoniaId || "").trim().toUpperCase();
            if (!coloniaId.startsWith(municipioId)) return;
            const suffix = coloniaId.slice(municipioId.length, municipioId.length + 3);
            if (/^\d{3}$/.test(suffix)) usados.add(Number(suffix));
          });

          const paginationData = result?.pagination || {};
          hasNext = Boolean(paginationData?.has_next);
          nextPage += 1;
        }

        let generado = "";
        for (let i = 1; i <= 999; i += 1) {
          if (usados.has(i)) continue;
          const candidato = `${municipioId}${String(i).padStart(3, "0")}`;
          const existente = await obtenerColoniaMantenimiento(authToken, candidato);
          if (coloniaIdRequestRef.current !== requestId) return;
          if (!existente) {
            generado = candidato;
            break;
          }
        }

        if (coloniaIdRequestRef.current !== requestId) return;
        setForm((prev) => ({ ...prev, coloniaId: generado }));
        if (!generado) {
          setError(`No hay IDs disponibles para el municipio ${municipioId}.`);
        }
      } catch (err) {
        if (coloniaIdRequestRef.current !== requestId) return;
        setForm((prev) => ({ ...prev, coloniaId: "" }));
        setError(err.message || "No se pudo generar el ID de colonia.");
      } finally {
        if (coloniaIdRequestRef.current === requestId) {
          setGeneratingColoniaId(false);
        }
      }
    },
    [authToken, editingId],
  );

  const handleSave = async () => {
    const coloniaId = String(form.coloniaId || "").trim().toUpperCase();
    const nombre = String(form.nombre || "").trim().toUpperCase();
    const fkMunicipioId = String(form.municipioId || "").trim();

    if (!coloniaId) {
      setError("Debe ingresar Colonia ID.");
      return;
    }
    if (!nombre) {
      setError("Debe ingresar nombre.");
      return;
    }
    if (!fkMunicipioId) {
      setError("Debe seleccionar municipio.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ColoniaId: coloniaId,
        Nombre: nombre,
        FKMunicipioId: fkMunicipioId,
      };
      if (editingId) {
        await actualizarColoniaMantenimiento(authToken, editingId, payload);
        setSuccess("Colonia actualizada correctamente.");
      } else {
        await crearColoniaMantenimiento(authToken, payload);
        setSuccess("Colonia creada correctamente.");
      }
      resetForm();
      setModalOpen(false);
      await loadData();
    } catch (err) {
      const msg = err?.message || "No se pudo guardar la colonia.";
      const isCreate = !editingId;
      const isDuplicateId =
        isCreate &&
        (Number(err?.status || 0) === 409 || /ya existe una colonia con ese id/i.test(String(msg)));

      if (isDuplicateId && fkMunicipioId) {
        await generarColoniaIdDisponible(fkMunicipioId);
        setError("El ID generado ya existía. Se asignó otro ID disponible, revise y guarde nuevamente.");
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c" }}>
              Colonias - Mantenimiento
            </Typography>
            <Typography sx={{ color: "#5a6a80", fontSize: 14 }}>
              Registros: {pagination.total}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || saving}
            >
              Recargar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              sx={{ backgroundColor: "#19498c" }}
            >
              Nuevo
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Filtro ID"
              value={filters.id}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, id: event.target.value.toUpperCase() }))
              }
              sx={{ width: { xs: "100%", md: 170 } }}
            />
            <TextField
              label="Filtro nombre"
              value={filters.nombre}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, nombre: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Filtro departamento"
              select
              value={filters.departamentoId}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  departamentoId: event.target.value,
                  municipioId: "",
                }))
              }
              sx={{ width: { xs: "100%", md: 250 } }}
            >
              <MenuItem value="">TODOS</MenuItem>
              {departamentos.map((row) => (
                <MenuItem key={row.DepartamentoId} value={row.DepartamentoId}>
                  {row.Nombre}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Filtro municipio"
              select
              value={filters.municipioId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, municipioId: event.target.value }))
              }
              sx={{ width: { xs: "100%", md: 250 } }}
            >
              <MenuItem value="">TODOS</MenuItem>
              {municipiosFiltradosFiltro.map((row) => (
                <MenuItem key={row.MunicipioId} value={row.MunicipioId}>
                  {row.Nombre}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={() => setFilters(EMPTY_FILTERS)}>
              Limpiar filtros
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#19498c" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Nombre</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Departamento</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Municipio</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: 120 }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin registros.
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((row) => (
                <TableRow key={row.ColoniaId}>
                  <TableCell>{row.ColoniaId}</TableCell>
                  <TableCell>{row.Nombre}</TableCell>
                  <TableCell>{row.DepartamentoNombre || row.FKDepartamentoId || ""}</TableCell>
                  <TableCell>{row.MunicipioNombre || row.FKMunicipioId || ""}</TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => handleEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={Number(pagination?.total || 0)}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const nextRowsPerPage = parseInt(event.target.value, 10) || 25;
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </Paper>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Editar Colonia" : "Nueva Colonia"}</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Colonia ID"
              value={form.coloniaId}
              InputProps={{
                readOnly: true,
                sx: {
                  bgcolor: "#f5f8ff",
                  "& .MuiInputBase-input": {
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    fontFamily: "monospace",
                    textAlign: "center",
                  },
                },
              }}
              helperText={
                editingId
                  ? "ID existente"
                  : generatingColoniaId
                    ? "Generando ID..."
                    : "Generado automáticamente"
              }
              FormHelperTextProps={{
                sx: {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              sx={{ width: { xs: "100%", md: 190 } }}
            />
            <TextField
              label="Nombre"
              value={form.nombre}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, nombre: event.target.value.toUpperCase() }))
              }
              fullWidth
            />
            <TextField
              label="Departamento"
              select
              value={form.departamentoId}
              onChange={(event) => {
                const value = event.target.value;
                setForm((prev) => ({
                  ...prev,
                  departamentoId: value,
                  municipioId: "",
                  coloniaId: "",
                }));
              }}
              sx={{ width: { xs: "100%", md: 260 } }}
            >
              <MenuItem value="">SELECCIONE</MenuItem>
              {departamentos.map((row) => (
                <MenuItem key={row.DepartamentoId} value={row.DepartamentoId}>
                  {row.Nombre}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Municipio"
              select
              value={form.municipioId}
              onChange={(event) => {
                const value = String(event.target.value || "").trim();
                setForm((prev) => ({
                  ...prev,
                  municipioId: value,
                  coloniaId: "",
                }));
                if (!editingId && value) {
                  void generarColoniaIdDisponible(value);
                }
              }}
              sx={{ width: { xs: "100%", md: 260 } }}
            >
              <MenuItem value="">SELECCIONE</MenuItem>
              {municipiosFiltradosForm.map((row) => (
                <MenuItem key={row.MunicipioId} value={row.MunicipioId}>
                  {row.Nombre}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading || generatingColoniaId}
            sx={{ backgroundColor: "#19498c" }}
          >
            {editingId ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
